/**
 * Manufacturers Tab
 * Displays extracted manufacturer information
 */

import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert
} from '@mui/material';
import { Business as ManufacturerIcon } from '@mui/icons-material';
import { Manufacturer } from '../../types/bom.types';

interface ManufacturersTabProps {
  manufacturers: Manufacturer[];
}

const ManufacturersTab: React.FC<ManufacturersTabProps> = ({ manufacturers }) => {
  if (!manufacturers || manufacturers.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">
          No manufacturer data available. Extract from a page first.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ManufacturerIcon color="primary" />
        <Typography variant="h6">
          Manufacturer Analysis ({manufacturers.length} manufacturers)
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Manufacturer</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Component Count</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Components</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Pages</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {manufacturers.map((manufacturer, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {manufacturer.manufacturer || 'Unknown'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={manufacturer.component_count || 0} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(manufacturer.components || []).slice(0, 5).map((comp, idx) => (
                      <Chip
                        key={idx}
                        label={comp}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                    {(manufacturer.components || []).length > 5 && (
                      <Chip
                        label={`+${manufacturer.components.length - 5} more`}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {(manufacturer.pages || []).join(', ')}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ManufacturersTab;

